import { Signal } from '@angular/core';
import { patchState, WritableStateSource } from '@ngrx/signals';
import { SLOW_LOADING_THRESHOLD_MS } from '../config/performance.config';

/**
 * State interface that must be implemented by stores using slow loading tracking
 */
export interface SlowLoadingState {
  loading: boolean;
  slowLoading: boolean;
  error: string | null;
}

/**
 * Helper to execute an async operation with slow loading timer
 * Sets up timer to trigger slowLoading state after threshold, and cleans up on completion
 * 
 * @param store - The signal store instance
 * @param writable - Writable state source for the store
 * @param operation - Async operation to execute
 * @returns Promise that resolves with the operation result
 */
export async function withSlowLoadingTimer<T, TState extends SlowLoadingState>(
  store: { loading: Signal<boolean> },
  writable: WritableStateSource<TState>,
  operation: () => Promise<T>
): Promise<T> {
  patchState(writable, { loading: true, slowLoading: false, error: null } as Partial<TState>);
  
  const slowLoadingTimer = setTimeout(() => {
    if (store.loading()) {
      patchState(writable, { slowLoading: true } as Partial<TState>);
    }
  }, SLOW_LOADING_THRESHOLD_MS);
  
  try {
    return await operation();
  } finally {
    clearTimeout(slowLoadingTimer);
    patchState(writable, { loading: false, slowLoading: false } as Partial<TState>);
  }
}
